import React, { useEffect, useState } from "react";
import CrudPage, { Badge } from "@/components/CrudPage";
import { formatKM, formatDate, monthLabel, todayISO, parseDecimal, toInputComma } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";

const TIPOVI = ["Povrat PDV-a", "Pretplata PDV-a"];
const STATUSI = ["Primljeno", "Očekivano"];

function Tile({ label, value, accent }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-extrabold font-heading tabular-nums mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

const isPovrat = (tip) => (tip || "").toLowerCase().startsWith("povrat");

export default function Pdv() {
  const [opening, setOpening] = useState({ date: "", amount: 0 });
  const [draft, setDraft] = useState({ date: "", amount: "" });

  const loadOpening = () =>
    api.get("/pdv-opening").then((r) => {
      setOpening(r.data);
      setDraft({ date: r.data.date || "", amount: toInputComma(r.data.amount) });
    }).catch(() => {});
  useEffect(() => { loadOpening(); }, []);

  const saveOpening = async () => {
    try {
      await api.put("/pdv-opening", { date: draft.date, amount: parseDecimal(draft.amount) });
      await loadOpening();
      toast.success("Početno stanje spremljeno");
    } catch (e) {
      toast.error("Greška pri spremanju početnog stanja");
    }
  };

  const compute = (r) => {
    const amt = Number(r.amount) || 0;
    return { ...r, _amt: amt, _signed: isPovrat(r.tip) ? amt : -amt };
  };

  const columns = [
    { key: "month", label: "Mjesec", render: (r) => (r.month ? `${monthLabel(r.month)} ${String(r.month).split("-")[0]}` : "—"), export: (r) => r.month },
    { key: "tip", label: "Tip", render: (r) => <Badge tone={isPovrat(r.tip) ? "success" : "warning"}>{r.tip || "—"}</Badge> },
    {
      key: "_amt", label: "Iznos",
      render: (r) => <span className={`tabular-nums font-semibold ${isPovrat(r.tip) ? "text-emerald-600" : "text-amber-600"}`}>{isPovrat(r.tip) ? "+" : "−"} {formatKM(r._amt)}</span>,
      export: (r) => r._signed,
    },
    { key: "date", label: "Datum", render: (r) => formatDate(r.date), export: (r) => r.date },
    { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "Primljeno" ? "info" : "muted"}>{r.status || "—"}</Badge> },
    { key: "note", label: "Napomena", render: (r) => r.note || "—" },
  ];

  const fields = [
    { key: "month", label: "Mjesec", type: "month", required: true },
    { key: "tip", label: "Tip", type: "select", options: TIPOVI, required: true },
    { key: "amount", label: "Iznos (KM)", type: "number", step: "0.01", required: true },
    { key: "date", label: "Datum (primljeno / uplaćeno)", type: "date" },
    { key: "status", label: "Status", type: "select", options: STATUSI },
    { key: "note", label: "Napomena", type: "textarea", full: true },
  ];

  const summary = (rows) => {
    const povrati = rows.filter((r) => isPovrat(r.tip)).reduce((a, b) => a + b._amt, 0);
    const pretplate = rows.filter((r) => !isPovrat(r.tip)).reduce((a, b) => a + b._amt, 0);
    const saldo = (Number(opening.amount) || 0) + pretplate - povrati;
    return (
      <div className="space-y-4" data-testid="pdv-summary">
        <div className="rounded-xl border bg-secondary/40 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Početno stanje PDV-a</div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Na dan</Label>
              <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="w-44" data-testid="pdv-opening-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Iznos (KM)</Label>
              <Input type="text" inputMode="decimal" placeholder="npr. 57725,45" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value.replace(/[^0-9.,-]/g, "") })} className="w-44" data-testid="pdv-opening-amount" />
            </div>
            <Button onClick={saveOpening} data-testid="pdv-opening-save"><Save className="h-4 w-4 mr-1.5" /> Spremi početno stanje</Button>
            {opening.date && (
              <span className="text-sm text-muted-foreground">Trenutno: {formatKM(opening.amount)} ({formatDate(opening.date)})</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Tile label="Početno stanje" value={formatKM(opening.amount)} accent="text-slate-900 dark:text-slate-100" />
          <Tile label="Ukupno pretplate" value={formatKM(pretplate)} accent="text-amber-600" />
          <Tile label="Saldo PDV-a" value={formatKM(saldo)} accent={saldo >= 0 ? "text-emerald-600" : "text-red-600"} />
        </div>
      </div>
    );
  };

  return (
    <CrudPage
      title="Povrat PDV-a"
      subtitle="Evidencija povrata i pretplate PDV-a po mjesecima, sa početnim stanjem i saldom"
      endpoint="pdv"
      columns={columns}
      fields={fields}
      testid="pdv"
      computeRow={compute}
      emptyDefaults={{ tip: "Povrat PDV-a", status: "Primljeno", month: todayISO().slice(0, 7), date: todayISO() }}
      filterOptions={{ key: "tip", label: "Tip", options: TIPOVI }}
      summary={summary}
    />
  );
}
